import React, { useEffect, useMemo, useState } from 'react';
import {
    Box, Paper, Typography, TextField, MenuItem, Chip, Checkbox, IconButton,
    FormControlLabel, Button, Stack, Tooltip, Alert, Skeleton,
} from '@mui/material';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import RelatorioViewer from './RelatorioViewer';
import api from '../services/api';
import { isAdmin } from '../services/auth';
import { relatoriosVisiveis } from '../config/relatorios';

/**
 * Relatório personalizável: um relatório do catálogo como base, as colunas que
 * o usuário escolher e os filtros que aquela base já aceita.
 *
 * <b>Por que uma base, e não um construtor sobre as entidades.</b> Cada
 * relatório do catálogo é uma consulta revisada, com as regras de apuração que
 * fazem o número valer (estorno no mês certo, glosa fora do "a receber", guia
 * cancelada fora da soma). Montar SELECT a partir dos campos do modelo jogaria
 * tudo isso fora e abriria caminho para ler campo clínico criptografado por
 * fora das regras de acesso. Aqui o recorte é o mesmo; o que muda é o que
 * aparece.
 *
 * A lista de colunas vem de `GET /api/relatorios/catalogo`: são ~200 colunas,
 * que não se copiam para o front sem divergir na coluna seguinte.
 */
export default function RelatorioPersonalizado() {
    const admin = useMemo(() => isAdmin(), []);
    const bases = useMemo(() => relatoriosVisiveis(admin), [admin]);

    const [catalogo, setCatalogo] = useState(null);
    const [erro, setErro] = useState(null);
    const [base, setBase] = useState('');
    const [escolhidas, setEscolhidas] = useState([]);

    useEffect(() => {
        api.get('/relatorios/catalogo')
            .then(r => setCatalogo(Array.isArray(r.data) ? r.data : []))
            .catch(() => setErro('Não foi possível carregar a lista de colunas. Recarregue a página.'));
    }, []);

    /** Colunas do relatório-base, na ordem em que o back-end as declara. */
    const colunasDaBase = useMemo(() => {
        const entrada = (catalogo || []).find(c => c.chave === base);
        return entrada?.colunas ?? [];
    }, [catalogo, base]);

    // Base nova começa com tudo marcado: é o relatório como ele já era, e
    // desmarcar é mais rápido que marcar dez colunas uma a uma.
    const trocarBase = (chave) => {
        setBase(chave);
        const entrada = (catalogo || []).find(c => c.chave === chave);
        setEscolhidas((entrada?.colunas ?? []).map(c => c.campo));
    };

    const alternar = (campo) => setEscolhidas(atual => (
        atual.includes(campo)
            ? atual.filter(c => c !== campo)
            : [...colunasDaBase.map(c => c.campo).filter(c => atual.includes(c) || c === campo)]
    ));

    const mover = (campo, passo) => setEscolhidas(atual => {
        const i = atual.indexOf(campo);
        const j = i + passo;
        if (i < 0 || j < 0 || j >= atual.length) return atual;
        const copia = [...atual];
        [copia[i], copia[j]] = [copia[j], copia[i]];
        return copia;
    });

    const restaurar = () => setEscolhidas(colunasDaBase.map(c => c.campo));

    const tituloDe = (campo) => colunasDaBase.find(c => c.campo === campo)?.titulo ?? campo;
    const baseSelecionada = bases.find(b => b.key === base);

    const seletor = (
        <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1.5 }}>
                Monte o relatório
            </Typography>

            {erro && <Alert severity="error" sx={{ mb: 2 }}>{erro}</Alert>}

            {!catalogo && !erro && <Skeleton variant="rounded" height={56} sx={{ mb: 2 }} />}

            {catalogo && (
                <TextField
                    select
                    size="small"
                    label="Relatório base"
                    value={base}
                    onChange={(e) => trocarBase(e.target.value)}
                    helperText="Os filtros e as regras de apuração são os do relatório escolhido."
                    sx={{ minWidth: 300, mb: 2 }}
                >
                    {bases.map(b => (
                        <MenuItem key={b.key} value={b.key}>
                            {b.titulo}
                            <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                                {b.categoria}
                            </Typography>
                        </MenuItem>
                    ))}
                </TextField>
            )}

            {base && (
                <>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, flexWrap: 'wrap' }}>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            Colunas
                        </Typography>
                        <Chip
                            size="small"
                            label={`${escolhidas.length} de ${colunasDaBase.length}`}
                            color={escolhidas.length === 0 ? 'default' : 'primary'}
                            variant="outlined"
                        />
                        <Button
                            size="small"
                            startIcon={<RestartAltIcon />}
                            onClick={restaurar}
                            disabled={escolhidas.length === colunasDaBase.length}
                        >
                            Todas
                        </Button>
                    </Box>

                    <Box
                        sx={{
                            display: 'grid',
                            gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' },
                            columnGap: 2,
                        }}
                    >
                        {colunasDaBase.map(c => (
                            <FormControlLabel
                                key={c.campo}
                                control={
                                    <Checkbox
                                        size="small"
                                        checked={escolhidas.includes(c.campo)}
                                        onChange={() => alternar(c.campo)}
                                    />
                                }
                                label={<Typography variant="body2">{c.titulo}</Typography>}
                            />
                        ))}
                    </Box>

                    {/* A ordem é a da tabela, do PDF e do CSV — por isso é
                        editável aqui, e não só na visualização. */}
                    {escolhidas.length > 1 && (
                        <Box sx={{ mt: 1.5 }}>
                            <Typography variant="caption" color="text.secondary">
                                Ordem das colunas
                            </Typography>
                            <Stack direction="row" sx={{ flexWrap: 'wrap', gap: 0.75, mt: 0.5 }}>
                                {escolhidas.map((campo, i) => (
                                    <Paper
                                        key={campo}
                                        variant="outlined"
                                        sx={{ display: 'flex', alignItems: 'center', pl: 1, gap: 0.25 }}
                                    >
                                        <Typography variant="caption" sx={{ fontWeight: 600 }}>
                                            {i + 1}. {tituloDe(campo)}
                                        </Typography>
                                        <Tooltip title="Mover para a esquerda">
                                            <IconButton
                                                size="small"
                                                aria-label={`Mover ${tituloDe(campo)} para a esquerda`}
                                                disabled={i === 0}
                                                onClick={() => mover(campo, -1)}
                                                sx={{ transform: 'rotate(-90deg)' }}
                                            >
                                                <ArrowUpwardIcon fontSize="inherit" />
                                            </IconButton>
                                        </Tooltip>
                                        <Tooltip title="Mover para a direita">
                                            <IconButton
                                                size="small"
                                                aria-label={`Mover ${tituloDe(campo)} para a direita`}
                                                disabled={i === escolhidas.length - 1}
                                                onClick={() => mover(campo, 1)}
                                                sx={{ transform: 'rotate(-90deg)' }}
                                            >
                                                <ArrowDownwardIcon fontSize="inherit" />
                                            </IconButton>
                                        </Tooltip>
                                    </Paper>
                                ))}
                            </Stack>
                        </Box>
                    )}

                    {escolhidas.length === 0 && (
                        <Alert severity="info" sx={{ mt: 1.5 }}>
                            Marque ao menos uma coluna para consultar.
                        </Alert>
                    )}
                </>
            )}
        </Paper>
    );

    // Sem base escolhida não há relatório para consultar: a tela mostra só o
    // montador, em vez de uma tabela vazia que não quer dizer nada.
    if (!base || escolhidas.length === 0) {
        return (
            <Box sx={{ p: { xs: 2, md: 3 } }}>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5, color: 'secondary.main' }}>
                    Relatório personalizável
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Escolha um relatório como base e as colunas que você quer ver.
                </Typography>
                {seletor}
            </Box>
        );
    }

    return (
        <RelatorioViewer
            key={base}
            reportKey={base}
            colunas={escolhidas}
            extra={seletor}
            tituloPagina={`Personalizável · ${baseSelecionada?.titulo ?? base}`}
        />
    );
}
